import classNames from 'classnames';
import React, {MouseEvent, useMemo, useState} from 'react';

import {ClickEvent, Tracker, TrackImpression} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Button, ButtonProps} from '@udemy/react-core-components';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {
    CustomPlanCard,
    EnterprisePlanCard,
    EnterprisePlanHeader,
    LeadershipPlanCard,
    LeadershipPlanHeader,
    PersonalPlanCard,
    PersonalPlanHeader,
    PlanComparisonWrapper,
    PlanComparisonWrapperSkeleton,
    TeamPlanCard,
    TeamPlanHeader,
} from '@udemy/react-plan-comparison';
import {BlockSkeleton} from '@udemy/react-reveal-components';
import {SubscriptionCTAClickEvent} from '@udemy/subscription-browse';
import {
    AnnualSubscriptionPlanPricingOption,
    DateIntervalType,
    SubscriptionPlanProductType,
    useConsumerSubscriptionPlan,
    useTeamSubscriptionPlan,
} from '@udemy/subscription-graphql';
import {useFormatCurrency, useUDData} from '@udemy/ud-data';

import {calculateNumCourses, useValuePlanExperimentLogic} from 'src/hooks/usePlanComparisonLogic';

import {VALUE_PLANSTARTER_PLAN} from '../../lib/experimentation-constants';
import {trackSubscriptionImpression, UI_REGION_MAIN} from '../comparison-matrix/eventing';
import {PlanComparisonHeader} from '../plan-comparison-header/plan-comparison-header.react-component';
import {
    PERSONAL_PLAN_CHECKOUT_URL,
    PERSONAL_PLAN_UPGRADE_URL,
    removeSubscribeFromPageCookie,
    STARTER_PLAN_CHECKOUT_URL,
    TEAM_PLAN_V1_FALLBACK_CURRENCY_FORMATTER,
    UB_REF_QUERY_PARAM_VALUE,
} from './constants';
import styles from './plan-comparison.module.less';

const assetPath = process.env.ASSET_PREFIX ?? '';

export const ComparePlanCta = (props: Pick<ButtonProps, 'onClick'>) => {
    const {gettext} = useI18n();
    const onClick = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();

        const anchorId = 'comparison-matrix-anchor';
        const targetElement = document.getElementById(anchorId);
        if (targetElement) {
            targetElement.scrollIntoView({behavior: 'smooth'});
        }

        Tracker.publishEvent(
            new ClickEvent({
                componentName: 'plansComparisonAnchorLink',
            }),
        );
        props.onClick?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        // Handle both Enter and Space key presses for accessibility
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick(event as unknown as MouseEvent<HTMLElement>);
        }
    };

    return (
        <Button
            udStyle={'ghost'}
            componentClass="a"
            onClick={onClick}
            onKeyDown={handleKeyDown}
            href="#comparison-matrix-anchor"
            tabIndex={0}
        >
            {gettext('Compare plans and features')}
            <Icon glyph="arrow-right" className={styles['cta-arrow']} label={false} />
        </Button>
    );
};

const PlanPriceText = ({listPrice, discountPrice}: {listPrice: string; discountPrice?: string}) => {
    const {gettext} = useI18n();

    return (
        <span className={classNames('ud-heading-lg', styles['price-text'])}>
            {gettext('Starting at')}{' '}
            {discountPrice && (
                <>
                    <span
                        className={classNames('ud-heading-xl', styles['price-text-strikethrough'])}
                    >
                        {listPrice}
                    </span>{' '}
                </>
            )}
            <span className="ud-heading-xl">{discountPrice ?? listPrice}</span>
            {gettext('/month')}
        </span>
    );
};

interface PlanComparisonProps {
    showUBLA: boolean;
}

export const PlanComparison = (props: PlanComparisonProps) => {
    const {gettext, interpolate} = useI18n();
    const {formatCurrency} = useFormatCurrency();
    const {Config: udConfig} = useUDData();
    const [personalPlanIsLoading, setPersonalPlanIsLoading] = useState(true);
    const [teamPlanIsLoading, setTeamPlanIsLoading] = useState(true);
    const [valuePlanIsLoading, setValuePlanIsLoading] = useState(true);
    const [personalPlanDiscountEnabled, setPersonalPlanDiscountEnabled] = useState(false);

    const {showUBLA} = props;

    const {getCtaText, isValuePlanSubscriber, valuePlanContext} = useValuePlanExperimentLogic();
    const personalPlanContext = useConsumerSubscriptionPlan();
    const teamPlanContext = useTeamSubscriptionPlan();

    const starterPlanCourseCount = useMemo(
        () => calculateNumCourses(valuePlanContext),
        [valuePlanContext],
    );

    const personalPlanCourseCount = useMemo(
        () => calculateNumCourses(personalPlanContext),
        [personalPlanContext],
    );

    const heroTitle = gettext('Subscribe to success with a Udemy plan');
    const heroSubtitle = gettext(
        'Level up your career or pick up a hobby on your time with top-rated courses from real-world experts.',
    );

    // Determine subscriber status for CTA logic
    const isPersonalPlanSubscriber =
        personalPlanContext && 'isSubscriberToSpecificPlan' in personalPlanContext
            ? personalPlanContext.isSubscriberToSpecificPlan ?? false
            : false;

    const PriceBlockSkeleton = () => (
        <BlockSkeleton className={styles['price-loading']} data-testid="price-loading" />
    );

    const {isLoading: isValuePlanExperimentLoading, featureVariantAssignments} = useFeatures([
        VALUE_PLANSTARTER_PLAN,
    ]);
    const isValuePlanExperimentEnabled =
        (!isValuePlanExperimentLoading &&
            featureVariantAssignments.get(VALUE_PLANSTARTER_PLAN)?.configuration
                ?.enableValuePlan) ||
        false;

    const valuePlanSubscription = useMemo(() => {
        if (!valuePlanContext && !valuePlanIsLoading) {
            return undefined;
        }
        if (valuePlanContext?.isLoading) {
            return {subscriptionPlan: undefined, priceComponent: <PriceBlockSkeleton />};
        }
        setValuePlanIsLoading(valuePlanContext?.isLoading ?? false);
        if (valuePlanContext && 'priceOption' in valuePlanContext) {
            const formattedMonthlyPrice = valuePlanContext.formattedMonthlyAmount;
            const discountMonthlyPrice =
                valuePlanContext.selectedDiscount?.subscriptionDiscountMonthlyPrice;

            const currency = valuePlanContext.monthlyPlanOption?.listPrice?.currency ?? '';
            const formattedDiscountMonthlyPrice = discountMonthlyPrice
                ? formatCurrency(discountMonthlyPrice, {currency})
                : undefined;
            return {
                subscriptionPlan: valuePlanContext,
                priceComponent: (
                    <PlanPriceText
                        listPrice={formattedMonthlyPrice}
                        discountPrice={formattedDiscountMonthlyPrice}
                    />
                ),
            };
        }
        return {subscriptionPlan: undefined, priceComponent: <PriceBlockSkeleton />};
    }, [valuePlanContext, valuePlanIsLoading, formatCurrency]);

    // The Personal Plan Price prop
    const personalSubscription = useMemo(() => {
        // Don't display card if there isn't a plan available
        if (!personalPlanContext && !personalPlanIsLoading) {
            return undefined;
        }
        if (personalPlanContext?.isLoading) {
            return {subscriptionPlan: undefined, priceComponent: <PriceBlockSkeleton />};
        }
        setPersonalPlanIsLoading(personalPlanContext?.isLoading ?? true);
        if (personalPlanContext && 'priceOption' in personalPlanContext) {
            setPersonalPlanDiscountEnabled(
                !!personalPlanContext?.selectedDiscount?.subscriptionDiscountMonthlyPrice,
            );
            const formattedMonthlyPrice = personalPlanContext.formattedMonthlyAmount;
            const discountMonthlyPrice =
                personalPlanContext.selectedDiscount?.subscriptionDiscountMonthlyPrice;
            setPersonalPlanDiscountEnabled(!!discountMonthlyPrice);

            const currency = personalPlanContext.monthlyPlanOption?.listPrice?.currency ?? '';
            const formattedDiscountMonthlyPrice = discountMonthlyPrice
                ? formatCurrency(discountMonthlyPrice, {currency})
                : undefined;
            return {
                subscriptionPlan: personalPlanContext,
                priceComponent: (
                    <PlanPriceText
                        listPrice={formattedMonthlyPrice}
                        discountPrice={formattedDiscountMonthlyPrice}
                    />
                ),
            };
        }
        return {subscriptionPlan: undefined, priceComponent: <PriceBlockSkeleton />};
    }, [personalPlanContext, personalPlanIsLoading, formatCurrency]);

    // The Team Plan Price prop
    const teamSubscription = useMemo(() => {
        // Don't display card if there isn't a plan available
        if (!teamPlanContext && !teamPlanIsLoading) {
            return undefined;
        }
        if (teamPlanContext?.isLoading) {
            return {subscriptionPlan: undefined, priceComponent: <PriceBlockSkeleton />};
        }
        setTeamPlanIsLoading(teamPlanContext?.isLoading ?? true);
        if (teamPlanContext && 'priceOption' in teamPlanContext) {
            const yearlyPlanOption = teamPlanContext.priceOption(
                DateIntervalType.Year,
            ) as AnnualSubscriptionPlanPricingOption;
            // HACK HACK HACK - this is a temporary solution to get the price to display for TPv1 b/c pricing won't support it at the GraphQL layer.
            // Specifically, the hardcoded TPv1 fallback price of $360.00 USD per year must be shown in USD, and must fully override the local currency formatter.
            // This is known to be a bad experience for users and an extreme anti-pattern, but again, pricing / subs platform will not support a fallback in the API layer
            // for TPv1.
            const useFallbackPriceFormatter = teamPlanContext?.id === '0';
            const price = useFallbackPriceFormatter
                ? formatCurrency(
                      yearlyPlanOption.listPrice.amount,
                      TEAM_PLAN_V1_FALLBACK_CURRENCY_FORMATTER,
                  )
                : formatCurrency(yearlyPlanOption?.monthlyPrice.amount, {
                      currency: yearlyPlanOption?.monthlyPrice.currency ?? '',
                  });
            const priceText = useFallbackPriceFormatter
                ? interpolate(gettext('%(price)s a year per user'), {price}, true)
                : interpolate(gettext('%(price)s a month per user'), {price}, true);
            return {
                subscriptionPlan: teamPlanContext,
                priceComponent: <p className="ud-heading-md">{priceText}</p>,
            };
        }
        return {subscriptionPlan: teamPlanContext, priceComponent: <PriceBlockSkeleton />};
    }, [teamPlanContext, teamPlanIsLoading, formatCurrency, gettext, interpolate]);

    // Render loading state for entire plan comparison module until consumer subscription plan loaded
    if (personalPlanIsLoading || valuePlanIsLoading) {
        return <PlanComparisonWrapperSkeleton />;
    }

    const teamSubscriptionPlan =
        teamSubscription?.subscriptionPlan &&
        !teamSubscription.subscriptionPlan.isLoading &&
        !teamSubscription.subscriptionPlan.isSubscriber
            ? teamSubscription.subscriptionPlan
            : undefined;
    const licenseContext = teamSubscriptionPlan?.licenseContext;
    const accordionPanels: {renderTitle: () => React.ReactElement; children: React.ReactNode}[] =
        [];

    if (isValuePlanExperimentEnabled) {
        accordionPanels.push(
            {renderTitle: () => <></>, children: null},
            {renderTitle: () => <></>, children: null},
        );
    } else {
        accordionPanels.push({
            renderTitle: () => (
                <PersonalPlanHeader
                    subscriptionDiscount={personalSubscription?.subscriptionPlan?.selectedDiscount}
                    hideIndividualTag={false}
                />
            ),
            children: personalSubscription && (
                <div className={styles['plan-card-accordion']}>
                    <PersonalPlanCard
                        subPlan={personalSubscription}
                        ubRef={UB_REF_QUERY_PARAM_VALUE}
                        hideIndividualTag={false}
                        isValuePlanComparison={false}
                    />
                </div>
            ),
        });
        accordionPanels.push({
            renderTitle: () => <TeamPlanHeader licenseContext={licenseContext} />,
            children: teamSubscription && (
                <TeamPlanCard subPlan={teamSubscription} ubRef={UB_REF_QUERY_PARAM_VALUE} />
            ),
        });
        accordionPanels.push({
            renderTitle: () => <EnterprisePlanHeader />,
            children: <EnterprisePlanCard ubRef={UB_REF_QUERY_PARAM_VALUE} />,
        });
        if (showUBLA) {
            accordionPanels.push({
                renderTitle: () => <LeadershipPlanHeader />,
                children: <LeadershipPlanCard ubRef={UB_REF_QUERY_PARAM_VALUE} />,
            });
        }
    }
    const valuePlanCtaLabel = getCtaText('value', isPersonalPlanSubscriber);
    const personalPlanCtaLabel = getCtaText('personal', isPersonalPlanSubscriber);

    const handleCtaClick = () => {
        removeSubscribeFromPageCookie();
    };

    return (
        <div>
            {!isValuePlanExperimentEnabled && <PlanComparisonHeader />}
            <div
                className={classNames(styles['plan-comparison'], {
                    [styles['experiment-plan-comparison']]: isValuePlanExperimentEnabled,
                })}
            >
                {isValuePlanExperimentEnabled && (
                    <picture className={styles['hero-bg-image']}>
                        <source
                            media="(min-width: 980px) and (min-resolution: 2dppx)"
                            srcSet={`${assetPath}/staticx/udemy/images/value-plan/tiers-desktop-2x.png`}
                        />
                        <source
                            media="(min-width: 980px)"
                            srcSet={`${assetPath}/staticx/udemy/images/value-plan/tiers-desktop.png`}
                        />
                        <source
                            media="(min-resolution: 2dppx)"
                            srcSet={`${assetPath}/staticx/udemy/images/value-plan/tiers-mobile-2x.png`}
                        />
                        <img
                            src={`${assetPath}/staticx/udemy/images/value-plan/tiers-mobile.png`}
                            alt=""
                            className={styles['hero-bg-image']}
                        />
                    </picture>
                )}
                {isValuePlanExperimentEnabled && (
                    <div className={styles['hero-text-mobile']}>
                        <h2 className={styles['hero-text-mobile-title']}>{heroTitle}</h2>
                        <p className={styles['hero-text-mobile-subtitle']}>{heroSubtitle}</p>
                    </div>
                )}
                <PlanComparisonWrapper
                    className={classNames(
                        'ud-container',
                        styles['plan-comparison-wrapper'],
                        styles['plan-cards-container'],
                    )}
                    title={undefined}
                    subtitle={undefined}
                    accordionPanels={accordionPanels}
                    showAccordionCardsOnMobile={!isValuePlanExperimentEnabled}
                >
                    {isValuePlanExperimentEnabled ? (
                        <>
                            <div className={styles['value-plan-card-offset']}>
                                <CustomPlanCard
                                    title={
                                        <span>
                                            {gettext('Starter')}{' '}
                                            <span style={{color: 'var(--color-teal-500)'}}>
                                                {gettext('Plan')}
                                            </span>
                                        </span>
                                    }
                                    subtitle={gettext(
                                        'Keep your business skills sharp or learn a new hobby.',
                                    )}
                                    featuresLabel={gettext("What's included")}
                                    features={[
                                        {
                                            text: interpolate(
                                                gettext(
                                                    '%(courseCount)s+ top-rated business, creative, and lifestyle courses',
                                                ),
                                                {
                                                    courseCount:
                                                        starterPlanCourseCount.toLocaleString(),
                                                },
                                                true,
                                            ),
                                            glyph: 'video',
                                        },
                                        {
                                            text: gettext(
                                                'Guided learning from Udemy AI Assistant',
                                            ),
                                            glyph: 'sparkles',
                                        },
                                        {
                                            text: gettext(
                                                'Quizzes and assignments to reinforce learning',
                                            ),
                                            glyph: 'help-support',
                                        },
                                        {
                                            text: gettext('Ad-supported'),
                                            glyph: 'info-outline',
                                        },
                                    ]}
                                    priceComponent={
                                        valuePlanSubscription?.subscriptionPlan ? (
                                            <TrackImpression
                                                trackFunc={() =>
                                                    trackSubscriptionImpression(
                                                        udConfig?.price_country?.id ?? '',
                                                        SubscriptionPlanProductType.ConsumersubscriptionValuePlan,
                                                        valuePlanSubscription.subscriptionPlan,
                                                        UI_REGION_MAIN,
                                                    )
                                                }
                                            >
                                                <div>{valuePlanSubscription.priceComponent}</div>
                                            </TrackImpression>
                                        ) : (
                                            valuePlanSubscription?.priceComponent
                                        )
                                    }
                                    ctaLabel={valuePlanCtaLabel}
                                    ctaHref={STARTER_PLAN_CHECKOUT_URL}
                                    ctaDisabled={isValuePlanSubscriber || isPersonalPlanSubscriber}
                                    onCtaClick={() => {
                                        handleCtaClick();
                                        const plan = valuePlanSubscription?.subscriptionPlan;
                                        if (plan) {
                                            const planPriceIds = plan.priceOptions.map(
                                                (option) => option.id,
                                            );
                                            Tracker.publishEvent(
                                                new SubscriptionCTAClickEvent(
                                                    planPriceIds[0],
                                                    SubscriptionPlanProductType.ConsumersubscriptionValuePlan,
                                                    null as never,
                                                    UI_REGION_MAIN,
                                                    null,
                                                    undefined,
                                                    planPriceIds,
                                                ),
                                            );
                                        }
                                    }}
                                />
                            </div>
                            <CustomPlanCard
                                title={
                                    <span>
                                        {gettext('Personal')}{' '}
                                        <span style={{color: 'var(--color-purple-300)'}}>
                                            {gettext('Plan')}
                                        </span>
                                    </span>
                                }
                                subtitle={gettext('Unlock your career potential.')}
                                subtitleColor="purple"
                                featuresLabel={gettext('Everything in Starter Plan plus')}
                                features={[
                                    {
                                        text: interpolate(
                                            gettext(
                                                '%(courseCount)s+ top-rated courses including AI, web development, data science, and IT',
                                            ),
                                            {
                                                courseCount:
                                                    personalPlanCourseCount.toLocaleString(),
                                            },
                                            true,
                                        ),
                                        glyph: 'video-group',
                                    },
                                    {
                                        text: gettext(
                                            'AI-powered soft skills practice through interactive role play scenarios',
                                        ),
                                        glyph: 'sparkles',
                                    },
                                    {
                                        text: gettext(
                                            'Hands-on technical practice through labs and coding exercises',
                                        ),
                                        glyph: 'console',
                                    },
                                    {
                                        text: gettext(
                                            'Comprehensive certification prep for AWS, Microsoft, CompTIA, PMI, and more',
                                        ),
                                        glyph: 'gift',
                                    },
                                    {
                                        text: gettext('Ad-free'),
                                        glyph: 'info-outline',
                                    },
                                ]}
                                priceComponent={
                                    personalSubscription?.subscriptionPlan ? (
                                        <TrackImpression
                                            trackFunc={() =>
                                                trackSubscriptionImpression(
                                                    udConfig?.price_country?.id ?? '',
                                                    SubscriptionPlanProductType.Consumersubscription,
                                                    personalSubscription.subscriptionPlan,
                                                    UI_REGION_MAIN,
                                                )
                                            }
                                        >
                                            <div>{personalSubscription.priceComponent}</div>
                                        </TrackImpression>
                                    ) : (
                                        personalSubscription?.priceComponent
                                    )
                                }
                                ctaLabel={personalPlanCtaLabel}
                                isRecommended={true}
                                ctaHref={
                                    isValuePlanSubscriber
                                        ? PERSONAL_PLAN_UPGRADE_URL
                                        : PERSONAL_PLAN_CHECKOUT_URL
                                }
                                ctaDisabled={isPersonalPlanSubscriber}
                                shadedAreaColor="purple"
                                onCtaClick={() => {
                                    handleCtaClick();
                                    const plan = personalSubscription?.subscriptionPlan;
                                    if (plan) {
                                        const planPriceIds = plan.priceOptions.map(
                                            (option) => option.id,
                                        );
                                        Tracker.publishEvent(
                                            new SubscriptionCTAClickEvent(
                                                planPriceIds[0],
                                                SubscriptionPlanProductType.Consumersubscription,
                                                null as never,
                                                UI_REGION_MAIN,
                                                null,
                                                undefined,
                                                planPriceIds,
                                            ),
                                        );
                                    }
                                }}
                            />
                        </>
                    ) : (
                        personalSubscription && (
                            <div className={styles['plan-card']}>
                                <PersonalPlanCard
                                    subPlan={personalSubscription}
                                    ubRef={UB_REF_QUERY_PARAM_VALUE}
                                    bannerText={undefined}
                                    ctaLabel={undefined}
                                    hideIndividualTag={false}
                                    isValuePlanComparison={false}
                                />
                            </div>
                        )
                    )}
                    {!isValuePlanExperimentEnabled && teamSubscription && (
                        <div className={styles['plan-card']}>
                            <TeamPlanCard
                                subPlan={teamSubscription}
                                ubRef={UB_REF_QUERY_PARAM_VALUE}
                                bottomPadding={personalPlanDiscountEnabled}
                            />
                        </div>
                    )}
                    {!isValuePlanExperimentEnabled && (
                        <div className={styles['plan-card']}>
                            <EnterprisePlanCard
                                ubRef={UB_REF_QUERY_PARAM_VALUE}
                                bottomPadding={personalPlanDiscountEnabled}
                            />
                        </div>
                    )}
                    {showUBLA && !isValuePlanExperimentEnabled && (
                        <div className={styles['plan-card']}>
                            <LeadershipPlanCard
                                ubRef={UB_REF_QUERY_PARAM_VALUE}
                                bottomPadding={personalPlanDiscountEnabled}
                            />
                        </div>
                    )}
                </PlanComparisonWrapper>
            </div>
        </div>
    );
};
